import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const articles = [
  {
    slug: '/blog/where-to-buy-used-cars-near-astoria-oregon',
    category: 'Local Buyer Guide',
    icon: '📍',
    title: 'Where Can I Buy a Used Car Near Astoria, Oregon?',
    excerpt:
      'An honest breakdown of every option for Clatsop County buyers — from local dealerships to auto brokers — and how to avoid overpaying in a market with limited competition.',
    readTime: '8 min read',
    tag: 'Astoria',
  },
  {
    slug: '/blog/most-reliable-cars-oregon-coast',
    category: 'Local Buyer Guide',
    icon: '🛡️',
    title: 'Most Reliable Cars for the Oregon Coast',
    excerpt:
      'The salt air, constant rain, and winding roads demand more from a vehicle. Here\'s what actually holds up — and what to avoid — based on years of buying and selling cars in Clatsop County.',
    readTime: '9 min read',
    tag: 'Oregon Coast',
  },
  {
    slug: '/blog/is-it-cheaper-to-buy-car-in-warrenton-than-portland',
    category: 'Local Buyer Guide',
    icon: '💵',
    title: 'Is It Cheaper to Buy a Car in Warrenton Than Portland?',
    excerpt:
      'Portland has more dealerships and more competition. So why do some buyers end up paying less in Warrenton? The answer has everything to do with how you buy.',
    readTime: '7 min read',
    tag: 'Pricing',
  },
  {
    slug: '/blog/how-to-buy-used-car-smart-way',
    category: 'Buyer Education',
    icon: '📖',
    title: 'How to Buy a Used Car the Smart Way',
    excerpt:
      'Practical tips from a team with 15+ years of hands-on auto industry experience. Avoid common pitfalls and drive home with confidence.',
    readTime: '12 min read',
    tag: 'Buyer Guide',
  },
];

function ArticleCardSkeleton() {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col h-full border border-gray-100" aria-hidden="true">
      {/* Banner skeleton */}
      <div className="h-32 bg-gradient-to-r from-[#1A2332] via-[#2C3E55] to-[#1A2332] animate-pulse flex-shrink-0" />
      {/* Content skeleton */}
      <div className="p-5 flex flex-col flex-1 gap-3">
        <div className="h-5 w-full bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
        <div className="h-4 w-5/6 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
        <div className="space-y-2 flex-1">
          <div className="h-3 w-full bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
          <div className="h-3 w-full bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
          <div className="h-3 w-4/5 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
        </div>
        <div className="flex items-center justify-between mt-auto pt-2">
          <div className="h-3 w-16 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
          <div className="h-4 w-20 bg-gradient-to-r from-[#F7F5F0] via-[#E8E2D6] to-[#F7F5F0] rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}

export default function LocalBuyerResources() {
  const { ref, isVisible } = useScrollAnimation();
  const [cardsReady, setCardsReady] = useState(false);

  // Trigger card reveal shortly after section enters viewport
  // so skeletons flash briefly then content fades in
  const handleSectionVisible = () => {
    if (!cardsReady) {
      setTimeout(() => setCardsReady(true), 400);
    }
  };

  return (
    <section
      className="hidden md:block py-20 bg-white border-t border-[#E8E2D6]"
      ref={(node) => {
        (ref as React.MutableRefObject<HTMLElement | null>).current = node;
        if (node && isVisible) handleSectionVisible();
      }}
      aria-label="Local buyer resources and guides"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="inline-flex items-center gap-2 bg-white border border-[#D8D0BC] rounded-full px-4 py-2 mb-5">
            <span aria-hidden="true" className="text-[#E8B84B]">🧭</span>
            <span className="text-sm font-medium text-[#6B7A8D] uppercase tracking-wider">Local Buyer Resources</span>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A2332] mb-5"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            Know Before You Buy —{' '}
            <span className="text-[#92650a]">Oregon Coast Guides</span>
          </h2>
          <p className="text-base sm:text-lg text-[#4A5568] max-w-3xl mx-auto leading-relaxed">
            Real answers to the questions <a href="/service-areas" className="text-[#7a5208] hover:text-[#1A2332] font-semibold underline decoration-1 underline-offset-2 transition-colors">Oregon Coast and SW Washington buyers</a> actually ask. No fluff, no sales pitch — just honest local knowledge. Buyers in <a href="/used-cars-astoria-oregon" className="text-[#7a5208] hover:text-[#1A2332] font-semibold underline decoration-1 underline-offset-2 transition-colors">Astoria</a>, <a href="/used-cars-seaside-oregon" className="text-[#7a5208] hover:text-[#1A2332] font-semibold underline decoration-1 underline-offset-2 transition-colors">Seaside</a>, and <a href="/used-cars-tillamook-oregon" className="text-[#7a5208] hover:text-[#1A2332] font-semibold underline decoration-1 underline-offset-2 transition-colors">Tillamook</a> rely on these guides before visiting the lot.
          </p>
        </div>

        {/* 4-Column Grid */}
        <div
          className={`grid grid-cols-2 lg:grid-cols-4 gap-5 mb-12 transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          onAnimationEnd={handleSectionVisible}
        >
          {!cardsReady
            ? articles.map((_, i) => <ArticleCardSkeleton key={i} />)
            : articles.map((article) => (
              <Link
                key={article.slug}
                to={article.slug}
                className="group flex flex-col"
              >
                <div className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col h-full border border-gray-100 hover:border-[#E8B84B]">
                  {/* Icon Banner */}
                  <div className="relative h-32 bg-gradient-to-br from-[#1A2332] to-[#2C3E55] flex flex-col items-center justify-center flex-shrink-0 gap-2">
                    <span className="w-14 h-14 flex items-center justify-center text-5xl" aria-hidden="true">
                      {article.icon}
                    </span>
                    <span className="bg-[#E8B84B] text-[#1A2332] px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                      {article.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-5 flex flex-col flex-1">
                    <h3
                      className="text-base font-bold text-[#1A2332] mb-2 group-hover:text-[#92650a] transition-colors leading-snug"
                      style={{ fontFamily: 'Oswald, sans-serif' }}
                    >
                      {article.title}
                    </h3>
                    <p className="text-sm text-[#4A5568] mb-4 line-clamp-3 flex-1">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-xs text-[#4A5568] flex items-center gap-1">
                        <span aria-hidden="true">⏱️</span>
                        {article.readTime}
                      </span>
                      <span className="text-[#E8B84B] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all whitespace-nowrap">
                        Read More
                        <span aria-hidden="true">→</span>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))
          }
        </div>

        {/* Bottom CTA Row */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 bg-[#1A2332] hover:bg-[#2C3E55] text-[#E8B84B] px-7 py-3.5 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <span aria-hidden="true">📰</span>
            View All Buyer Guides
          </Link>
          <Link
            to="/service-areas"
            className="inline-flex items-center gap-2 bg-white hover:bg-[#F7F5F0] text-[#1A2332] px-7 py-3.5 rounded-lg font-semibold border border-[#E8E2D6] hover:border-[#E8B84B] transition-all duration-300 whitespace-nowrap"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <span aria-hidden="true">🗺️</span>
            All Service Areas
          </Link>
        </div>

      </div>
    </section>
  );
}
